/**
 * Product catalogue — the single source of truth for every product page,
 * category page, navigation menu and search index on the public site.
 *
 * Raw content is extracted from the legacy page files into
 * `catalog.generated.json` (see scripts/extract-catalog.js). This module adds
 * types, category metadata, display-name fixes and helpers on top.
 */
import raw from './catalog.generated.json';
import { PRODUCT_MEDIA, cutout, packCutout } from './productMedia';

export type CategoryKey = 'interior' | 'exterior' | 'undercoat' | 'metal' | 'wood' | 'waterproofing' | 'other';

export interface Benefit {
  title: string;
  text: string;
  icon?: string | null;
  emoji?: string | null;
}

export interface CatalogProduct {
  /** Route is the unique key (two legacy pages can share a title). */
  route: string;
  slug: string;
  title: string;
  tagline: string | null;
  category: CategoryKey;
  image: string | null;
  heroImage: string | null;
  features: string[];
  benefits: Benefit[];
  intro: string | null;
  tds: string | null;
  warranty?: string;
  /** Legacy duplicate pages that still resolve but are not listed in grids. */
  hidden?: boolean;
  group?: string;
}

export interface ApplicationStep {
  title: string;
  text: string;
}

export interface TextureItem {
  name: string;
  image: string;
  warranty?: string;
  href: string;
}

export interface CatalogCategory {
  key: CategoryKey;
  route: string;
  name: string;
  shortName: string;
  eyebrow: string;
  tagline: string;
  description: string;
  heroImage: string;
  useFor: string[];
  steps: ApplicationStep[];
  groups?: string[];
  products: CatalogProduct[];
  textures?: TextureItem[];
}

/* ------------------------------------------------------------------ */
/* Category metadata                                                   */
/* ------------------------------------------------------------------ */

type CategoryMeta = Omit<CatalogCategory, 'products' | 'textures'>;

const CATEGORY_META: Record<CategoryKey, CategoryMeta> = {
  interior: {
    key: 'interior',
    route: '/InteriorWallPaint',
    name: 'Interior Wall Paints',
    shortName: 'Interior Paints',
    eyebrow: 'Interior paints',
    tagline: 'Interiors that stay fresh long after the last coat',
    description:
      'Smooth, washable emulsions and decorative finishes formulated for real homes — fingerprints, humidity and repeated cleaning included.',
    heroImage: '/images/web/auto/interior-banner.jpg',
    useFor: ['Living rooms and bedrooms', 'Kitchens and dining spaces', 'Ceilings and feature walls', 'Offices, shops and hospitality interiors'],
    steps: [
      { title: 'Prepare the surface', text: 'Clean the wall, fill cracks and dents with wall putty, then sand to a smooth, dust-free finish.' },
      { title: 'Prime', text: 'Apply one coat of a Vertex interior primer or sealer to even out absorption and improve topcoat adhesion.' },
      { title: 'Apply two coats', text: 'Thin as directed on the technical data sheet and apply two coats, allowing the recommended recoat interval.' },
      { title: 'Let it cure', text: 'Keep the room ventilated. Full hardness and washability develop over the first few days.' },
    ],
  },
  exterior: {
    key: 'exterior',
    route: '/ExteriorWallPaint',
    name: 'Exterior Wall Paints',
    shortName: 'Exterior Paints',
    eyebrow: 'Exterior paints',
    tagline: 'Built to take the weather, year after year',
    description:
      'UV-stable, water-repellent exterior emulsions and textures graded for Indian sun, monsoon and dust — so façades hold their colour without chalking or peeling.',
    heroImage: '/images/Exterior-banner.jpeg',
    useFor: ['Exterior masonry and plaster', 'Boundary walls and compound walls', 'Balconies, parapets and chajjas', 'Commercial façades and housing projects'],
    steps: [
      { title: 'Clean and repair', text: 'Wash off dust, chalk and algae. Remove loose paint and repair cracks with an exterior-grade filler.' },
      { title: 'Prime or base coat', text: 'Apply an exterior primer or Vertex Base Coat to seal the surface and control alkalinity and efflorescence.' },
      { title: 'Apply two coats', text: 'Apply two coats of exterior emulsion by brush, roller or spray, following the dilution on the data sheet.' },
      { title: 'Protect while it dries', text: 'Avoid painting in rain or direct midday heat. Allow the film to cure fully before it faces heavy weather.' },
    ],
  },
  undercoat: {
    key: 'undercoat',
    route: '/UnderCoat',
    name: 'Primers, Putties & Sealers',
    shortName: 'Undercoats',
    eyebrow: 'Undercoat systems',
    tagline: 'The foundation every good finish is built on',
    description:
      'Primers, wall putties and sealers that level the surface, lock out moisture and give your topcoat the grip and coverage it was designed for.',
    heroImage: '/images/web/room-yellow.jpg',
    useFor: ['New plaster before first paint', 'Repaints over old, chalky or patchy surfaces', 'Metal and wood before enamel or PU finishes', 'Damp-prone walls needing a sealing layer'],
    steps: [
      { title: 'Prepare the surface', text: 'Remove loose material, dust and grease. Neutralise fresh plaster and let it cure as recommended.' },
      { title: 'Fill and level', text: 'Apply wall putty in thin coats to fill dents and undulations, sanding between coats.' },
      { title: 'Prime or seal', text: 'Brush or roll on the primer or sealer evenly. One coat is usually enough; porous walls may need a second.' },
      { title: 'Move to the topcoat', text: 'Once dry, the surface is ready for the finish coat. Check the recoat interval on the data sheet.' },
    ],
    groups: ['Primers', 'Wall putties', 'Sealers & base coats'],
  },
  metal: {
    key: 'metal',
    route: '/MetalPaints',
    name: 'Metal Paints & Enamels',
    shortName: 'Metal Paints',
    eyebrow: 'Metal paints',
    tagline: 'Rust protection with a finish that shines',
    description:
      'Anti-corrosive primers, gloss enamels, PU finishes and metallic effects for gates, grills, railings, machinery and industrial steel.',
    heroImage: '/images/web/house-retro.jpg',
    useFor: ['Gates, grills and railings', 'Doors, window frames and furniture', 'Agricultural and industrial equipment', 'Structural steel and tanks'],
    steps: [
      { title: 'Remove rust and scale', text: 'Wire-brush or sand the metal back to a sound surface, then wipe clean of dust and oil.' },
      { title: 'Apply an anti-corrosive primer', text: 'Use MG Red Oxide, Zinc Chromate or Zinc Phosphate primer to protect against rust.' },
      { title: 'Apply two coats of finish', text: 'Brush or spray two coats of enamel or PU finish, allowing the recommended drying time between coats.' },
      { title: 'Allow to harden', text: 'Keep the surface free of handling and moisture until the film has fully hardened.' },
    ],
  },
  wood: {
    key: 'wood',
    route: '/WoodPaints',
    name: 'Wood Paints & Finishes',
    shortName: 'Wood Paints',
    eyebrow: 'Wood finishes',
    tagline: 'Protect the grain, or paint it any colour you like',
    description:
      'Clear PU coatings, varnishes, primer surfacers and enamels that seal timber against moisture while bringing out its natural warmth.',
    heroImage: '/images/web/auto/woodpaint-banner.jpg',
    useFor: ['Doors, windows and shutters', 'Furniture and cabinetry', 'Wooden panelling and ceilings', 'Outdoor timber and pergolas'],
    steps: [
      { title: 'Sand and fill', text: 'Sand along the grain, fill nail holes and open grain with a suitable filler, then dust off.' },
      { title: 'Prime or seal', text: 'Apply a PU primer surfacer for painted finishes, or a sealer coat for clear finishes.' },
      { title: 'Build the finish', text: 'Apply two or more thin coats, lightly sanding between coats for a glass-smooth result.' },
      { title: 'Cure before use', text: 'Let the final coat harden fully before the surface goes back into service.' },
    ],
  },
  waterproofing: {
    key: 'waterproofing',
    route: '/WaterProofingAndConstructionChemicals',
    name: 'Waterproofing & Construction Chemicals',
    shortName: 'Waterproofing',
    eyebrow: 'Waterproofing',
    tagline: 'Stop damp at the source',
    description:
      'Roof coatings, damp-proof putties, polymer additives, tile adhesives and repair products that keep water out of the structure for good.',
    heroImage: '/images/web/house-dusk.jpg',
    useFor: ['Terraces, roofs and chajjas', 'Bathrooms, kitchens and wet areas', 'Basements and water tanks', 'Damp, salt-affected or leaking walls'],
    steps: [
      { title: 'Find the source', text: 'Trace where water is entering — cracks, joints, porous surfaces or standing water — before treating the symptom.' },
      { title: 'Repair and prepare', text: 'Clean thoroughly, open and fill cracks, and let repairs cure before coating.' },
      { title: 'Apply the system', text: 'Apply the waterproofing product in the coats and coverage specified on its data sheet, working joints and corners first.' },
      { title: 'Protect and cure', text: 'Allow full curing before exposing the area to water or foot traffic.' },
    ],
  },
  other: {
    key: 'other',
    route: '/OtherSurface',
    name: 'Specialty & Other Surfaces',
    shortName: 'Other Surfaces',
    eyebrow: 'Specialty coatings',
    tagline: 'Coatings for the surfaces regular paint can’t handle',
    description: 'Specialised sealers and coatings for concrete, stone, asbestos and other demanding substrates.',
    heroImage: '/images/web/house-cutting-edge.jpg',
    useFor: ['Concrete and cement sheets', 'Stone and brick', 'Terrace and floor surfaces', 'Industrial substrates'],
    steps: [
      { title: 'Assess the surface', text: 'Confirm the substrate is sound, clean and dry, and check the product’s data sheet for compatibility.' },
      { title: 'Prepare', text: 'Remove loose material and contaminants; repair defects with a compatible filler.' },
      { title: 'Apply', text: 'Apply in the specified number of coats and coverage rate for the surface.' },
      { title: 'Cure', text: 'Allow the recommended curing time before service.' },
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Display-name fixes for typos in the source pages                    */
/* ------------------------------------------------------------------ */

const NAME_FIXES: [RegExp, string][] = [
  [/Emlusion/gi, 'Emulsion'],
  [/Emerland/gi, 'Emerald'],
  [/Zin Chromate/gi, 'Zinc Chromate'],
  [/ZINCHROMATE/gi, 'Zinc Chromate'],
  [/Zin Phosphate/gi, 'Zinc Phosphate'],
  [/MG-?\s?RED\s?OX(ide|IDE)/gi, 'MG Red Oxide'],
  [/\bWOOD GUARD\b/g, 'Wood Guard'],
  [/\bTRUE WOOD\b/g, 'True Wood'],
  [/\bHI SHEEN\b/g, 'Hi-Sheen'],
  [/\b1k\b/g, '1K'],
  [/\b3IN1\b/gi, '3-in-1'],
  [/\s+-\s+/g, ' – '],
  [/\(Water Repellent Paint\)/g, ''],
];
const fixName = (s: string) => NAME_FIXES.reduce((acc, [re, to]) => acc.replace(re, to), s).replace(/\s+/g, ' ').trim();

const categoryFromRoute = (route: string): CategoryKey => {
  if (route.startsWith('/InteriorWallPaint')) return 'interior';
  if (route.startsWith('/ExteriorWallPaint') || route.startsWith('/Exterior/')) return 'exterior';
  if (route.startsWith('/UnderCoat')) return 'undercoat';
  if (route.startsWith('/MetalPaints')) return 'metal';
  if (route.startsWith('/WoodPaints')) return 'wood';
  if (route.startsWith('/WaterProofing/')) return 'waterproofing';
  return 'other';
};

const groupFor = (category: CategoryKey, title: string): string | undefined => {
  if (category !== 'undercoat') return undefined;
  if (/putty/i.test(title)) return 'Wall putties';
  if (/primer|oxide|chromate|phosphate/i.test(title)) return 'Primers';
  return 'Sealers & base coats';
};

/** Legacy duplicate routes — resolvable, but superseded by a better page. */
const HIDDEN_ROUTES = new Set(['/Exterior/BDSeal', '/Exterior/FineSquare', '/Exterior/SilkShine', '/Exterior/UltraWrp', '/ExteriorWallPaint/PrimerSurfaceBrown']);

/** Performance-warranty claims as shown on the original exterior range page. */
const WARRANTY: Record<string, string> = {
  '/ExteriorWallPaint/FineSquareExteriorEmulsion': 'Up to 15 years',
  '/ExteriorWallPaint/SilkShine_SuperGloss': 'Up to 6 years',
  '/ExteriorWallPaint/TitaniumOne': 'Up to 4 years',
  '/ExteriorWallPaint/UltraWRPExteriorEmulsion': 'Up to 4 years',
};

/* ------------------------------------------------------------------ */
/* Build                                                               */
/* ------------------------------------------------------------------ */

type RawProduct = (typeof raw.products)[number];
type RawListing = { name: string; image: string | null; href: string; features: string[] };

const listingByHref: Record<string, RawListing> = {};
Object.values(raw.categories as Record<string, { products: RawListing[] }>).forEach((c) =>
  c.products.forEach((p) => {
    listingByHref[p.href] = p;
  })
);

const slugOf = (route: string) =>
  route
    .replace(/^\//, '')
    .replace(/\//g, '-')
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .toLowerCase();

export const PRODUCTS: CatalogProduct[] = (raw.products as RawProduct[]).map((p) => {
  const listing = listingByHref[p.route];
  const category = categoryFromRoute(p.route);
  const title = fixName(p.title);
  const features = (listing?.features?.length ? listing.features : p.benefits.slice(0, 3).map((b) => b.title)).map(fixName);
  return {
    route: p.route,
    slug: slugOf(p.route),
    title,
    tagline: p.tagline ? fixName(p.tagline) : null,
    category,
    // Official pack shots (Sep 2026, background removed) take precedence over the legacy cut-outs.
    image: (PRODUCT_MEDIA[p.route]?.pack && cutout(PRODUCT_MEDIA[p.route]!.pack!.src, true)) || p.image || listing?.image || null,
    heroImage: p.heroImage,
    features,
    benefits: p.benefits.map((b) => ({ ...b, title: fixName(b.title) })),
    intro: p.intro,
    tds: p.tds,
    warranty: WARRANTY[p.route],
    hidden: HIDDEN_ROUTES.has(p.route),
    group: groupFor(category, title),
  };
});

const EXTERIOR_TEXTURES: TextureItem[] = [
  { name: 'Silk Shine Special Effect', image: '/images/textures/TextureSpecialEffect.png', warranty: 'Up to 15 years', href: '/InteriorWallPaint/SilkShineSpecialEffectpaints' },
  { name: 'Fine Square Waterproof Rustic Texture', image: '/images/textures/rusticperlina.png', warranty: 'Up to 6 years', href: '/WaterProofing/WaterproofingTexture' },
  { name: 'Fine Square Waterproof Perlina Texture', image: '/images/textures/rusticperlina.png', warranty: 'Up to 4 years', href: '/WaterProofing/WaterproofingTexture' },
];

/** Category listing order follows the original range pages. */
const listingOrder: Record<CategoryKey, string[]> = Object.fromEntries(
  Object.entries(raw.categories as Record<string, { products: RawListing[] }>).map(([k, c]) => [k, c.products.map((p) => p.href)])
) as Record<CategoryKey, string[]>;

export const CATEGORIES: Record<CategoryKey, CatalogCategory> = Object.fromEntries(
  (Object.keys(CATEGORY_META) as CategoryKey[]).map((key) => {
    const order = listingOrder[key] || [];
    const products = PRODUCTS.filter((p) => p.category === key && !p.hidden).sort((a, b) => {
      const ia = order.indexOf(a.route);
      const ib = order.indexOf(b.route);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    return [key, { ...CATEGORY_META[key], products, ...(key === 'exterior' ? { textures: EXTERIOR_TEXTURES } : {}) }];
  })
) as Record<CategoryKey, CatalogCategory>;

export const CATEGORY_LIST: CatalogCategory[] = (['interior', 'exterior', 'undercoat', 'metal', 'wood', 'waterproofing', 'other'] as CategoryKey[]).map((k) => CATEGORIES[k]);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export const getProduct = (route: string): CatalogProduct | undefined => PRODUCTS.find((p) => p.route === route);
export const getCategory = (key: CategoryKey): CatalogCategory => CATEGORIES[key];

export const relatedProducts = (product: CatalogProduct, limit = 4): CatalogProduct[] =>
  CATEGORIES[product.category].products.filter((p) => p.route !== product.route && p.title !== product.title).slice(0, limit);

export const searchCatalog = (query: string, limit = 8): CatalogProduct[] => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const score = (p: CatalogProduct) => {
    const t = p.title.toLowerCase();
    if (t === q) return 0;
    if (t.startsWith(q)) return 1;
    if (t.includes(q)) return 2;
    if (p.features.some((f) => f.toLowerCase().includes(q))) return 3;
    if (CATEGORIES[p.category].name.toLowerCase().includes(q)) return 4;
    return 99;
  };
  return PRODUCTS.filter((p) => !p.hidden && score(p) < 99)
    .sort((a, b) => score(a) - score(b))
    .slice(0, limit);
};

/** Technical data sheets (name + PDF). */
export type TdsSheet = {
  name: string;
  url: string;
  /** Page to link the card to when no catalogue product owns the sheet. */
  page?: string;
  pageLabel?: string;
  /** Thumbnail to show instead of a pack shot. */
  image?: string;
  category?: CategoryKey;
};

/** Sheets that belong to a range or a page rather than a single product. */
const EXTRA_SHEETS: TdsSheet[] = [
  {
    name: 'Marmovella application guide',
    url: '/public/assets/TDS/Marmovella%20Guide.pdf',
    page: '/Marmovella',
    pageLabel: 'Marmovella · Italian mineral stone finish',
    image: '/images/marmovella/marmovella-buckets-cutout.webp',
    category: 'other',
  },
];

export const TDS_SHEETS: TdsSheet[] = (raw.tds as { name: string; url: string }[]).map((t) => ({
  name:
    t.name ||
    decodeURIComponent(t.url.split('/').pop() || '')
      .replace(/\.pdf/gi, '')
      .replace(/_/g, ' ')
      .trim(),
  url: t.url,
})).concat(EXTRA_SHEETS);

/** Exterior colour moods and house formats — the entry points to the 22 scheme pages. */
export const EXTERIOR_MOODS = [
  { title: 'Peaceful & Serene', image: '/images/web/auto/peacefulserene.jpg', to: '/Exterior/PeacefulSerene', blurb: 'Soft, calming neutrals for homes that want to feel restful from the street.' },
  { title: 'Trendy & Stylish', image: '/images/web/auto/trendystylish.jpg', to: '/Exterior/TrendyStylish', blurb: 'Confident contemporary combinations with crisp contrasts.' },
  { title: 'Soft & Relaxing', image: '/images/web/auto/softrelaxing.jpg', to: '/Exterior/SoftRelaxing', blurb: 'Muted pastels and warm greys that age gracefully in strong sun.' },
  { title: 'Exciting & Bright', image: '/images/web/auto/excitingbright.jpg', to: '/Exterior/ExcitingBright', blurb: 'Saturated accents that make a façade the landmark on its lane.' },
];

export const HOUSE_FORMATS = [
  { title: 'Crafted Cube', image: '/images/Crafted-Cube.png', to: '/Exterior/CraftedCube' },
  { title: 'Roman Heritage', image: '/images/Roman-Heritage.png', to: '/Exterior/RomanHeritage' },
  { title: 'Cutting Edge', image: '/images/Cutting-edge.png', to: '/Exterior/CuttingEdge' },
  { title: 'Country Style', image: '/images/Country-style.png', to: '/Exterior/CountryStyle' },
  { title: 'Elegant Simplicity', image: '/images/Elegant-Simplicity.png', to: '/Exterior/ElegantSimplicity' },
  { title: 'Retro Regal', image: '/images/Retro-Regal.png', to: '/Exterior/RetroRegal' },
  { title: 'Eternal Temple', image: '/images/Eternal-Temple.png', to: '/Exterior/EternalTemple' },
  { title: 'Modern Geometry', image: '/images/Modern-Geometry.png', to: '/Exterior/ModernGeometry' },
  { title: 'Gradual Elevations', image: '/images/Gradual-Elevations.png', to: '/Exterior/GradualElevations' },
  { title: 'Retro Modern', image: '/images/Retro-Modern.png', to: '/Exterior/RetroModern' },
  { title: 'Beach House', image: '/images/Beach-House.png', to: '/Exterior/BeachHouse' },
  { title: 'Lattice House', image: '/images/Lattice-House.png', to: '/Exterior/LatticeHouse' },
  { title: 'Modern Baroque', image: '/images/Modern-Baroque.png', to: '/Exterior/ModernBaroque' },
  { title: 'Country House', image: '/images/Country-House.png', to: '/Exterior/CountryHouse' },
  { title: 'Grooved Design', image: '/images/Grooved-Design.png', to: '/Exterior/GroovedDesign' },
  { title: 'Iconic Villa', image: '/images/Iconic-Villa.png', to: '/Exterior/IconicVilla' },
  { title: 'Twisting Maze', image: '/images/Twisting-Maze.png', to: '/Exterior/TwistingMaze' },
  { title: 'Arched Residence', image: '/images/Arched-Residence.png', to: '/Exterior/ArchedResidence' },
];

/** Popular interior shades with room photography (from the original interior range page). */
export const INTERIOR_POPULAR_SHADES = [
  { name: 'Moonlit Blue', hex: '#507BB2', image: '/images/507bb2-L.jpeg' },
  { name: 'Mint Glade', hex: '#77B98B', image: '/images/77b98b-l.jpeg' },
  { name: 'Pink Whimsy', hex: '#F6EDE9', image: '/images/f6ede9-l.jpeg' },
  { name: 'Dusky Petal', hex: '#E1A5CA', image: '/images/E1A5CA-l.jpeg' },
];

/* ------------------------------------------------------------------ */
/* Admin (CMS) overrides                                               */
/* ------------------------------------------------------------------ */

/** A product as stored in the admin (see server/routes/catalog.js). */
export interface RemoteProduct {
  route: string;
  category: CategoryKey;
  title: string;
  tagline: string | null;
  intro: string | null;
  features: string[];
  benefits: { title: string; text: string }[];
  packImage: string | null;
  packCutout: string | null;
  gallery: { src: string; alt: string }[];
  tdsUrl: string | null;
  warranty: string | null;
  group: string | null;
  hidden: boolean;
  sortOrder: number;
}

const ALL_KEYS = Object.keys(CATEGORY_META) as CategoryKey[];

/**
 * Replace the built-in catalogue with the admin's. Mutates the exported arrays
 * in place (before the first render — see src/data/remote.ts) so every page,
 * menu and search box picks it up without further wiring.
 */
export function applyRemoteProducts(rows: RemoteProduct[]) {
  const next: CatalogProduct[] = rows
    .filter((r) => r.route && ALL_KEYS.includes(r.category))
    .map((r) => {
      delete PRODUCT_MEDIA[r.route];
      if (r.packImage || r.gallery.length) {
        PRODUCT_MEDIA[r.route] = {
          pack: r.packImage ? { src: r.packImage, alt: `${r.title} pack`, cutout: r.packCutout || undefined } : undefined,
          gallery: r.gallery,
        };
      }
      const pack = PRODUCT_MEDIA[r.route]?.pack;
      return {
        route: r.route,
        slug: slugOf(r.route),
        title: r.title,
        tagline: r.tagline,
        category: r.category,
        image: pack ? packCutout(pack, true) : null,
        heroImage: null,
        features: r.features.length ? r.features : r.benefits.slice(0, 3).map((b) => b.title),
        benefits: r.benefits,
        intro: r.intro,
        tds: r.tdsUrl,
        warranty: r.warranty || undefined,
        hidden: r.hidden,
        group: r.group || groupFor(r.category, r.title),
      };
    });
  if (!next.length) return;
  PRODUCTS.splice(0, PRODUCTS.length, ...next);
  const order = new Map(rows.map((r) => [r.route, r.sortOrder]));
  ALL_KEYS.forEach((key) => {
    const list = PRODUCTS.filter((p) => p.category === key && !p.hidden).sort((a, b) => (order.get(a.route) ?? 0) - (order.get(b.route) ?? 0));
    CATEGORIES[key].products.splice(0, CATEGORIES[key].products.length, ...list);
  });
}

export function applyRemoteTds(docs: { name: string; url: string }[]) {
  TDS_SHEETS.splice(0, TDS_SHEETS.length, ...docs.map((d) => ({ name: d.name, url: d.url })));
}

/** The built-in catalogue in the admin's shape — sent once by "Import website content". */
export const catalogSnapshot = (): RemoteProduct[] =>
  ALL_KEYS.flatMap((key) => {
    const listed = CATEGORIES[key].products;
    const extra = PRODUCTS.filter((p) => p.category === key && !listed.includes(p));
    return [...listed, ...extra].map((p, i) => {
      const media = PRODUCT_MEDIA[p.route];
      return {
        route: p.route,
        category: p.category,
        title: p.title,
        tagline: p.tagline,
        intro: p.intro,
        features: p.features,
        benefits: p.benefits.map((b) => ({ title: b.title, text: b.text })),
        packImage: media?.pack?.src || p.image,
        packCutout: media?.pack?.cutout || null,
        gallery: media?.gallery || [],
        tdsUrl: p.tds,
        warranty: p.warranty || null,
        group: p.group || null,
        hidden: Boolean(p.hidden),
        sortOrder: i,
      };
    });
  });
